import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {DataService} from '../../../Services/data.service';
import {StorageService} from '../../../Services/storage.service';
import {DatePipe} from '@angular/common';
import {BsToastService} from '../../../Services/bs-toast-service';
import {Title} from '@angular/platform-browser';
import {ChartOptionsModel} from '../../../DataModels/apexchart.model';

@Component({
  selector: 'app-inventory-dashboard',
  templateUrl: './inventory-dashboard.component.html',
  styleUrls: ['./inventory-dashboard.component.css'],
  standalone: false
})
export class InventoryDashboardComponent implements OnInit {

  isLoading = true;
  permissions: any = {};

  // KPI cards
  totalSkus = 0;
  totalValue = 0;
  belowReorder = 0;
  outOfStock = 0;
  overstocked = 0;
  pendingPOs = 0;

  // Reorder table
  reorderItems: any[] = [];

  // Overstock table
  overstockItems: any[] = [];

  // Slow-moving / dead stock
  slowMovingDays = 60;
  slowMovingItems: any[] = [];
  loadingSlowMoving = false;

  // Inventory count status
  countStatusItems: any[] = [];

  // Recent activity
  recentActivities: any[] = [];

  // Charts
  public categoryChartOptions: Partial<ChartOptionsModel> = {};
  public trendChartOptions: Partial<ChartOptionsModel> = {};
  public topValueChartOptions: Partial<ChartOptionsModel> = {};
  public topMovementChartOptions: Partial<ChartOptionsModel> = {};
  public turnoverChartOptions: Partial<ChartOptionsModel> = {};

  trendDays = 30;
  topValueDays = 30;
  topMovementDays = 30;
  turnoverDays = 30;

  // Chart empty-state flags
  categoryHasData = false;
  trendHasData = false;
  topValueHasData = false;
  topMovementHasData = false;
  turnoverHasData = false;


  constructor(
    private tableDataService: DataService,
    private localStorage: StorageService,
    private datePipe: DatePipe,
    private notification: BsToastService,
    private titleService: Title,
    private router: Router
  ) {
    this.permissions = JSON.parse(this.localStorage.StorageGet('Permissions', ''));
    this.initEmptyCharts();
  }

  ngOnInit(): void {
    this.titleService.setTitle('Inventory Dashboard');
    this.loadAll();
  }

  loadAll(): void {
    this.isLoading = true;
    this.loadKpis();
    this.loadReorderItems();
    this.loadSlowMoving();
    this.loadCountStatus();
    this.loadRecentActivity();
    this.loadCategoryChart();
    this.loadTrendChart();
    this.loadTopValueChart();
    this.loadTopMovementChart();
    this.loadTurnoverChart();
    this.loadOverstockItems();
  }

  loadKpis(): void {
    const companyId = this.localStorage.StorageGet('default_company_id', '');
    const params = companyId ? '?company_id=' + encodeURIComponent(companyId) : '';
    this.tableDataService.getData('inventory/dashboard/kpis' + params).subscribe({
      next: (result: any) => {
        this.totalSkus = result?.totalSkus ?? 0;
        this.totalValue = result?.totalValue ?? 0;
        this.belowReorder = result?.belowReorder ?? 0;
        this.outOfStock = result?.outOfStock ?? 0;
        this.pendingPOs = result?.pendingPOs ?? 0;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  loadReorderItems(): void {
    const companyId = this.localStorage.StorageGet('default_company_id', '');
    const params = companyId ? '?company_id=' + encodeURIComponent(companyId) : '';
    this.tableDataService.getData('inventory/dashboard/reorder-items' + params).subscribe({
      next: (result: any[]) => {
        this.reorderItems = result ?? [];
      },
      error: () => {
      }
    });
  }

  createPO(item: any): void {
    const state = {
      entityId: item.vendor_id ?? item.vendorId ?? '',
      entityName: item.preferred_vendor ?? item.preferredVendor ?? '',
      locationId: item.location_id ?? item.locationId ?? this.localStorage.StorageGet('default_location_id', ''),
      companyId: item.company_id ?? item.companyId ?? this.localStorage.StorageGet('default_company_id', ''),
      companyName: item.company_name ?? item.companyName ?? '',
      items: [{
        item_id: item.item_id ?? item.itemId ?? '',
        itemNumber: item.itemNumber ?? item.item_number ?? '',
        quantity: item.suggestedOrderQty ?? 0
      }]
    };
    this.router.navigate(['/transactions/invoice/po/form'], {
      queryParams: {documentId: 'newPurchaseOrder', batchId: ''},
      state
    }).then(() => {
    });
  }

  loadOverstockItems(): void {
    const companyId = this.localStorage.StorageGet('default_company_id', '');
    const params = companyId ? '?company_id=' + encodeURIComponent(companyId) : '';
    this.tableDataService.getData('inventory/dashboard/overstock-items' + params).subscribe({
      next: (result: any[]) => {
        this.overstockItems = result ?? [];
        this.overstocked = this.overstockItems.length;
      },
      error: () => {
      }
    });
  }

  loadSlowMoving(): void {
    this.loadingSlowMoving = true;
    const companyId = this.localStorage.StorageGet('default_company_id', '');
    const companyParam = companyId ? '&company_id=' + encodeURIComponent(companyId) : '';
    this.tableDataService.getData('inventory/dashboard/slow-moving?days=' + this.slowMovingDays + companyParam).subscribe({
      next: (result: any[]) => {
        this.slowMovingItems = result ?? [];
        this.loadingSlowMoving = false;
      },
      error: () => {
        this.loadingSlowMoving = false;
      }
    });
  }

  onSlowMovingDaysChange(): void {
    this.loadSlowMoving();
  }

  loadCountStatus(): void {
    const companyId = this.localStorage.StorageGet('default_company_id', '');
    const params = companyId ? '?company_id=' + encodeURIComponent(companyId) : '';
    this.tableDataService.getData('inventory/dashboard/count-status' + params).subscribe({
      next: (result: any[]) => {
        this.countStatusItems = result ?? [];
      },
      error: () => {
      }
    });
  }

  loadRecentActivity(): void {
    this.tableDataService.getData('activity?entity=inventory&pageNumber=0&pageSize=20').subscribe({
      next: (result: any) => {
        const raw: any[] = result?.Data ?? result ?? [];
        this.recentActivities = raw.map((a: any) => ({
          ...a,
          timelineIcon: this.resolveActivityIcon(a.batch_type_id),
          variant: a.variant ?? 'primary',
          text: a.text ?? a.message,
          mainText: a.mainText ?? a.batch_name,
        }));
      },
      error: () => {
      }
    });
  }

  private resolveActivityIcon(batchTypeId: number): string {
    switch (batchTypeId) {
      case 1:
        return 'mdi mdi-receipt text-success';
      case 2:
        return 'mdi mdi-currency-usd text-success';
      case 3:
        return 'mdi mdi-currency-usd text-success';
      case 4:
        return 'mdi mdi-currency-usd text-success';
      case 5:
        return 'mdi mdi-cash-refund text-success';
      case 6:
        return 'mdi mdi-airplane text-success';
      case 7:
        return 'mdi mdi-currency-usd text-success';
      case 8:
        return 'mdi mdi-currency-usd text-success';
      case 9:
        return 'mdi mdi-currency-usd text-success';
      case 10:
        return 'mdi mdi-currency-usd text-success';
      case 11:
        return 'mdi mdi-truck-delivery text-success';  // sales shipment
      case 12:
        return 'mdi mdi-truck-delivery text-success';  // purchase shipment
      case 13:
        return 'bi bi-basket text-success';             // inventory count AJE
      case 14:
        return 'bi bi-tools text-success';              // assembly
      case 15:
        return 'bi bi-arrow-left-right text-success';   // transfer
      case 16:
        return 'mdi mdi-chart-bar text-success';        // statistical entry
      case 18:
        return 'mdi mdi-currency-usd text-success';     // work order
      case 97:
        return 'mdi mdi-cash-register text-success';
      case 99:
        return 'mdi mdi-cash-register text-success';
      case 130:
        return 'bi bi-basket text-success';      // inventory count
      default:
        return 'mdi mdi-crosshairs-question text-danger';
    }
  }

  loadCategoryChart(): void {
    this.categoryHasData = false;
    const companyId = this.localStorage.StorageGet('default_company_id', '');
    const params = companyId ? '?company_id=' + encodeURIComponent(companyId) : '';
    this.tableDataService.getData('inventory/dashboard/value-by-category' + params).subscribe({
      next: (result: any) => {
        const labels: string[] = result?.map((r: any) => r.category) ?? [];
        const values: number[] = result?.map((r: any) => r.value) ?? [];
        this.categoryHasData = values.length > 0;
        this.categoryChartOptions = {
          series: values,
          chart: {type: 'donut', height: 320, background: 'transparent'},
          labels: labels,
          title: {text: 'Inventory Value by Category'},
          legend: {position: 'bottom'},
          dataLabels: {enabled: true}
        };
      },
      error: () => {
      }
    });
  }

  loadTrendChart(): void {
    this.trendHasData = false;
    const companyId = this.localStorage.StorageGet('default_company_id', '');
    const companyParam = companyId ? '&company_id=' + encodeURIComponent(companyId) : '';
    this.tableDataService.getData('inventory/dashboard/value-trend?days=' + this.trendDays + companyParam).subscribe({
      next: (result: any) => {
        const dates: string[] = result?.map((r: any) => r.date) ?? [];
        const values: number[] = result?.map((r: any) => r.value) ?? [];
        this.trendHasData = values.length > 0;
        this.trendChartOptions = {
          series: [{name: 'Inventory Value', data: values}],
          chart: {type: 'line', height: 320, background: 'transparent', toolbar: {show: true}},
          xaxis: {type: 'category', categories: dates},
          title: {text: 'Stock Level Trend'},
          dataLabels: {enabled: false},
          grid: {}
        };
      },
      error: () => {
      }
    });
  }

  onTrendDaysChange(): void {
    this.loadTrendChart();
  }

  onTopValueDaysChange(): void {
    this.loadTopValueChart();
  }

  onTopMovementDaysChange(): void {
    this.loadTopMovementChart();
  }

  onTurnoverDaysChange(): void {
    this.loadTurnoverChart();
  }

  loadTopValueChart(): void {
    this.topValueHasData = false;
    const companyId = this.localStorage.StorageGet('default_company_id', '');
    const companyParam = companyId ? '&company_id=' + encodeURIComponent(companyId) : '';
    this.tableDataService.getData('inventory/dashboard/top-by-value?days=' + this.topValueDays + companyParam).subscribe({
      next: (result: any) => {
        const items: string[] = result?.map((r: any) => r.item) ?? [];
        const values: number[] = result?.map((r: any) => r.value) ?? [];
        this.topValueHasData = values.length > 0;
        this.topValueChartOptions = {
          series: [{name: 'Extended Value', data: values}],
          chart: {type: 'bar', height: 320, background: 'transparent', toolbar: {show: false}},
          plotOptions: {bar: {horizontal: true}},
          xaxis: {type: 'category', categories: items},
          title: {text: 'Top 10 Items by Value'},
          dataLabels: {enabled: false},
          grid: {}
        };
      },
      error: () => {
      }
    });
  }

  loadTopMovementChart(): void {
    this.topMovementHasData = false;
    const companyId = this.localStorage.StorageGet('default_company_id', '');
    const companyParam = companyId ? '&company_id=' + encodeURIComponent(companyId) : '';
    this.tableDataService.getData('inventory/dashboard/top-by-movement?days=' + this.topMovementDays + companyParam).subscribe({
      next: (result: any) => {
        const items: string[] = result?.map((r: any) => r.item) ?? [];
        const qtys: number[] = result?.map((r: any) => r.qty) ?? [];
        this.topMovementHasData = qtys.length > 0;
        this.topMovementChartOptions = {
          series: [{name: 'Units Moved', data: qtys}],
          chart: {type: 'bar', height: 320, background: 'transparent', toolbar: {show: false}},
          plotOptions: {bar: {horizontal: true}},
          xaxis: {type: 'category', categories: items},
          title: {text: 'Top 10 Items by Movement'},
          dataLabels: {enabled: false},
          grid: {}
        };
      },
      error: () => {
      }
    });
  }

  loadTurnoverChart(): void {
    this.turnoverHasData = false;
    const companyId = this.localStorage.StorageGet('default_company_id', '');
    const companyParam = companyId ? '&company_id=' + encodeURIComponent(companyId) : '';
    this.tableDataService.getData('inventory/dashboard/turnover-by-category?days=' + this.turnoverDays + companyParam).subscribe({
      next: (result: any) => {
        const categories: string[] = result?.map((r: any) => r.category) ?? [];
        this.turnoverHasData = categories.length > 0;
        const rates: number[] = result?.map((r: any) => r.rate) ?? [];
        this.turnoverChartOptions = {
          series: [{name: 'Turnover Rate', data: rates}],
          chart: {type: 'bar', height: 320, background: 'transparent', toolbar: {show: false}},
          plotOptions: {bar: {horizontal: false}},
          xaxis: {type: 'category', categories: categories},
          title: {text: 'Inventory Turnover Rate by Category'},
          dataLabels: {enabled: false},
          grid: {}
        };
      },
      error: () => {
      }
    });
  }

  private initEmptyCharts(): void {
    const emptyBar: Partial<ChartOptionsModel> = {
      series: [{name: ' ', data: []}],
      chart: {type: 'bar', height: 320, background: 'transparent'},
      xaxis: {type: 'category', categories: []},
      title: {text: ' '},
      dataLabels: {enabled: false},
      grid: {}
    };
    this.trendChartOptions = {...emptyBar, chart: {...emptyBar.chart, type: 'line'}};
    this.topValueChartOptions = {...emptyBar};
    this.topMovementChartOptions = {...emptyBar};
    this.turnoverChartOptions = {...emptyBar};
    this.categoryChartOptions = {
      series: [],
      chart: {type: 'donut', height: 320, background: 'transparent'},
      labels: [],
      title: {text: ' '},
      dataLabels: {enabled: false}
    };
  }
}
